import { useEffect, useState } from 'react';
import { useChatContext } from 'stream-chat-react';
import { Channel } from 'stream-chat';
import * as Sentry from '@sentry/browser';
import { ChatGenerics } from '../streamTypes';
import { useAutoCreateChatRoom } from '../hooks/useAutoCreateChatRoom';

export interface UseSidebarChannelResult {
  channel?: Channel<ChatGenerics>;
  loading: boolean;
  error?: Error;
  isRemovedFromRoom: boolean;
}

/**
 * Loads a single known channel for the chat sidebar.
 *
 * The sidebar always targets one channel by id, so it retrieves it with
 * `channel.watch()` instead of `queryChannels()` (PROC-4133): queryChannels is
 * a list/search endpoint and is not performant for fetching a single channel.
 *
 * The room-existence "knock" (useAutoCreateChatRoom) runs first so we never
 * watch a channel the current user isn't a member of yet.
 */
export const useSidebarChannel = (channelId: string): UseSidebarChannelResult => {
  const { client } = useChatContext<ChatGenerics>('useSidebarChannel');
  const {
    loading: isRoomChecking,
    error: roomError,
    isRemovedFromRoom,
  } = useAutoCreateChatRoom(channelId);

  const [channel, setChannel] = useState<Channel<ChatGenerics>>();
  const [watchError, setWatchError] = useState<Error>();

  useEffect(() => {
    // Wait for the knock to finish (and skip on error) before watching.
    if (isRoomChecking || roomError) {
      return undefined;
    }

    let cancelled = false;
    const newChannel = client.channel('messaging', channelId);
    newChannel.watch()
      .then(() => {
        if (!cancelled) {
          setChannel(newChannel);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setWatchError(err as Error);
        }
        Sentry.captureException(err);
      });

    return () => {
      cancelled = true;
    };
  }, [client, channelId, isRoomChecking, roomError]);

  return {
    channel,
    loading: isRoomChecking || (!channel && !watchError),
    error: roomError || watchError,
    isRemovedFromRoom,
  };
};
